import React, { useRef, useState } from "react";
import { UserService } from "../../services/userService";
import { Toast } from "primereact/toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Password } from "primereact/password";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

type Props = {};

const ChangePassword: React.FC = (props: Props) => {
  const toastRef = useRef<Toast>(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required("Old Password is required"),
    newPassword: Yup.string()
      .min(8, "New Password must be at least 8 characters")
      .matches(/[a-z]/, "At least one lowercase letter")
      .matches(/[A-Z]/, "At least one uppercase letter")
      .matches(/[0-9]/, "At least one numeric digit")
      .required("New Password is required"),
    confirmNew: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords do not match")
      .required("Confirm New Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmNew: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const payload = {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        };
        const res = await UserService.changePassword(payload);
        if (res.status === -1) {
          toastRef.current?.show({
            severity: "warn",
            summary: "Update Password Failed",
            detail: res.message,
          });
          return;
        }
        toastRef.current?.show({
          severity: "success",
          summary: "Update Password Successful",
          detail: res.message,
        });

        Cookies.set("access_token", "", {
          expires: new Date(0),
          path: "/",
          domain: "localhost",
        });

        navigate("/auth");
      } catch (err: any) {
        const error =
          err.response?.data?.message || "An error occurred. Please try again.";
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: error,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordHeader = <div className="font-bold mb-3">Pick a password</div>;
  const passwordFooter = (
    <>
      <Divider />
      <p className="mt-2">Suggestions</p>
      <ul className="pl-2 ml-2 mt-0 line-height-3">
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li>Minimum 8 characters</li>
      </ul>
    </>
  );

  const footer = (
    <Button
      label="Save Changes"
      icon="pi pi-check"
      loading={submitting}
      disabled={!formik.isValid || !formik.dirty || submitting}
      className="bg-primary-500 hover:bg-primary-600 text-white font-semibold"
      type="submit"
      onClick={() => formik.handleSubmit()}
    />
  );

  return (
    <div className="w-full lg:w-10/12 mx-auto login">
      <Toast ref={toastRef} position="top-center" />
      <Card footer={footer} className="md:w-25rem shadow-none">
        <form onSubmit={formik.handleSubmit}>
          <div className="flex-col gap-4 w-full">
            <div className="field mb-4">
              <label
                htmlFor="oldPassword"
                className="block text-sm mb-1 font-semibold"
              >
                Old Password *
              </label>
              <Password
                id="oldPassword"
                name="oldPassword"
                value={formik.values.oldPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                toggleMask
                feedback={false}
                className={`w-full p-2 border rounded-md ${
                  formik.touched.oldPassword && formik.errors.oldPassword
                    ? "p-invalid border-red-500"
                    : "border-gray-300"
                }`}
              />
              {formik.touched.oldPassword && formik.errors.oldPassword && (
                <Message
                  severity="error"
                  text={formik.errors.oldPassword}
                  className="input-message"
                />
              )}
            </div>

            <div className="field mb-4">
              <label
                htmlFor="newPassword"
                className="block text-sm mb-1 font-semibold"
              >
                New Password *
              </label>
              <Password
                id="newPassword"
                name="newPassword"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                toggleMask
                header={passwordHeader}
                footer={passwordFooter}
                className={`w-full p-2 border rounded-md ${
                  formik.touched.newPassword && formik.errors.newPassword
                    ? "p-invalid border-red-500"
                    : "border-gray-300"
                }`}
              />
              {formik.touched.newPassword && formik.errors.newPassword && (
                <Message
                  severity="error"
                  text={formik.errors.newPassword}
                  className="input-message"
                />
              )}
            </div>

            <div className="field mb-4">
              <label
                htmlFor="confirmNew"
                className="block text-sm mb-1 font-semibold"
              >
                Confirm New Password *
              </label>
              <Password
                id="confirmNew"
                name="confirmNew"
                value={formik.values.confirmNew}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                toggleMask
                feedback={false}
                className={`w-full p-2 border rounded-md ${
                  formik.touched.confirmNew && formik.errors.confirmNew
                    ? "p-invalid border-red-500"
                    : "border-gray-300"
                }`}
              />
              {formik.touched.confirmNew && formik.errors.confirmNew && (
                <Message
                  severity="error"
                  text={formik.errors.confirmNew}
                  className="input-message"
                />
              )}
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ChangePassword;
